import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { signIn, signUp } from 'aws-amplify/auth';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Eye, EyeOff, Loader2 } from 'lucide-react';
import { analytics } from '@/lib/analytics';
import { useToast } from '@/hooks/use-toast';
import { EmailVerificationModal } from './EmailVerificationModal';
import { ForgotPasswordModal } from './ForgotPasswordModal';
import { ResetPasswordConfirmModal } from './ResetPasswordConfirmModal';

const loginSchema = z.object({
  email: z.string().email('Please enter a valid email address'),
  password: z.string().min(8, 'Password must be at least 8 characters'),
});

const registerSchema = z.object({
  email: z.string().email('Please enter a valid email address'),
  password: z.string().min(8, 'Password must be at least 8 characters'),
});

type LoginFormValues = z.infer<typeof loginSchema>;
type RegisterFormValues = z.infer<typeof registerSchema>;

interface AuthModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  defaultMode?: 'login' | 'register';
  onSuccess?: () => void;
}

export function AuthModal({ open, onOpenChange, defaultMode = 'login', onSuccess }: AuthModalProps) {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [mode, setMode] = useState<'login' | 'register'>(defaultMode);
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [verificationModalOpen, setVerificationModalOpen] = useState(false);
  const [forgotPasswordModalOpen, setForgotPasswordModalOpen] = useState(false);
  const [resetPasswordConfirmModalOpen, setResetPasswordConfirmModalOpen] = useState(false);
  const [registeredEmail, setRegisteredEmail] = useState('');
  const [resetEmail, setResetEmail] = useState('');

  const form = useForm<LoginFormValues | RegisterFormValues>({
    resolver: zodResolver(mode === 'login' ? loginSchema : registerSchema),
    defaultValues: {
      email: '',
      password: '',
    },
  });

  const onSubmit = async (data: LoginFormValues | RegisterFormValues) => {
    setIsLoading(true);

    try {
      // Track analytics
      analytics.event(mode === 'login' ? 'login_attempt' : 'register_attempt', {
        method: 'email',
      });

      if (mode === 'login') {
        // Login with AWS Amplify
        await signIn({ username: data.email, password: data.password });

        // On successful login
        analytics.conversion('login_success');

        toast({
          title: 'Success',
          description: 'Logged in successfully',
        });

        onOpenChange(false);
        form.reset();

        // Navigate to dashboard
        if (onSuccess) {
          onSuccess();
        } else {
          navigate('/app');
        }
      } else {
        // Registration with AWS Amplify
        await signUp({
          username: data.email,
          password: data.password,
          options: {
            userAttributes: {
              email: data.email,
            },
          },
        });

        // On successful registration
        analytics.conversion('register_success');

        toast({
          title: 'Verification required',
          description: 'Please check your email for the verification code',
        });

        // Show email verification modal for new registrations
        setRegisteredEmail(data.email);
        onOpenChange(false);
        setVerificationModalOpen(true);
        form.reset();
      }
    } catch (error) {
      console.error(`${mode} error:`, error);

      // Handle Cognito errors
      const errorMessage = error instanceof Error ? error.message : 'An error occurred';
      let userFriendlyMessage = errorMessage;

      // Map common Cognito errors to user-friendly messages
      if (errorMessage.includes('UserNotFoundException') || errorMessage.includes('User does not exist')) {
        userFriendlyMessage = 'User does not exist';
      } else if (errorMessage.includes('NotAuthorizedException') || errorMessage.includes('Incorrect username or password')) {
        userFriendlyMessage = 'Incorrect email or password';
      } else if (errorMessage.includes('UserNotConfirmedException')) {
        userFriendlyMessage = 'Please verify your email first';
        // Show verification modal for unconfirmed users
        setRegisteredEmail(data.email);
        onOpenChange(false);
        setVerificationModalOpen(true);
      } else if (errorMessage.includes('UsernameExistsException')) {
        userFriendlyMessage = 'An account with this email already exists';
      } else if (errorMessage.includes('InvalidPasswordException')) {
        userFriendlyMessage = 'Password does not meet requirements';
      } else if (errorMessage.includes('LimitExceededException')) {
        userFriendlyMessage = 'Too many attempts. Please try again later';
      }

      toast({
        title: 'Error',
        description: userFriendlyMessage,
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleModeSwitch = () => {
    const newMode = mode === 'login' ? 'register' : 'login';
    setMode(newMode);
    form.reset();
    analytics.event('auth_mode_switch', { from: mode, to: newMode });
  };

  const handleForgotPasswordBack = () => {
    setForgotPasswordModalOpen(false);
    onOpenChange(true);
    setMode('login');
  };

  const handleCodeSent = (email: string) => {
    setResetEmail(email);
    setForgotPasswordModalOpen(false);
    setResetPasswordConfirmModalOpen(true);
  };

  const handleResetPasswordBack = () => {
    setResetPasswordConfirmModalOpen(false);
    setForgotPasswordModalOpen(true);
  };

  const handleResetPasswordSuccess = () => {
    setResetPasswordConfirmModalOpen(false);
    onOpenChange(true);
    setMode('login');
  };

  const handleVerificationComplete = () => {
    setVerificationModalOpen(false);

    toast({
      title: 'Success',
      description: 'Account verified successfully',
    });

    // Navigate to dashboard
    if (onSuccess) {
      onSuccess();
    } else {
      navigate('/app');
    }
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="sm:max-w-[425px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-2xl">
            {mode === 'login' ? 'Welcome back' : 'Create an account'}
          </DialogTitle>
          <DialogDescription>
            {mode === 'login'
              ? 'Enter your credentials to continue'
              : 'Sign up to start receiving form notifications in Telegram'}
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4 mt-4">
            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Email</FormLabel>
                  <FormControl>
                    <Input
                      type="email"
                      placeholder="you@example.com"
                      autoComplete="email"
                      disabled={isLoading}
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="password"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Password</FormLabel>
                  <FormControl>
                    <div className="relative">
                      <Input
                        type={showPassword ? 'text' : 'password'}
                        autoComplete={mode === 'login' ? 'current-password' : 'new-password'}
                        disabled={isLoading}
                        {...field}
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                        tabIndex={-1}
                      >
                        {showPassword ? (
                          <EyeOff className="h-4 w-4" />
                        ) : (
                          <Eye className="h-4 w-4" />
                        )}
                      </button>
                    </div>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {mode === 'login' && (
              <div className="text-right">
                <Button
                  variant="link"
                  className="px-0 text-sm h-auto"
                  type="button"
                  onClick={() => {
                    analytics.event('forgot_password_click');
                    onOpenChange(false);
                    setForgotPasswordModalOpen(true);
                  }}
                >
                  Forgot password?
                </Button>
              </div>
            )}

            <Button type="submit" className="w-full" disabled={isLoading}>
              {isLoading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  {mode === 'login' ? 'Logging in...' : 'Creating account...'}
                </>
              ) : (
                <>{mode === 'login' ? 'Log in' : 'Sign up'}</>
              )}
            </Button>
          </form>
        </Form>

        <div className="text-center mt-6 p-3 bg-blue-50 rounded-lg border border-blue-100">
          <p className="text-sm text-gray-700">
            {mode === 'login' ? "Don't have an account? " : 'Already have an account? '}
            <button
              type="button"
              onClick={handleModeSwitch}
              className="text-blue-600 hover:text-blue-800 hover:underline font-semibold focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 rounded"
              disabled={isLoading}
            >
              {mode === 'login' ? 'Sign up for free' : 'Log in'}
            </button>
          </p>
        </div>

        {mode === 'register' && (
          <p className="text-xs text-center text-muted-foreground mt-4">
            By signing up, you agree to our{' '}
            <a href="/terms" className="text-primary hover:underline">
              Terms of Service
            </a>{' '}
            and{' '}
            <a href="/privacy" className="text-primary hover:underline">
              Privacy Policy
            </a>
          </p>
        )}
        </DialogContent>
      </Dialog>

      {/* Email Verification Modal */}
      <EmailVerificationModal
        open={verificationModalOpen}
        onOpenChange={setVerificationModalOpen}
        email={registeredEmail}
        onVerified={handleVerificationComplete}
      />

      {/* Forgot Password Modal */}
      <ForgotPasswordModal
        open={forgotPasswordModalOpen}
        onOpenChange={setForgotPasswordModalOpen}
        onBackToLogin={handleForgotPasswordBack}
        onCodeSent={handleCodeSent}
      />

      {/* Reset Password Confirm Modal */}
      <ResetPasswordConfirmModal
        open={resetPasswordConfirmModalOpen}
        onOpenChange={setResetPasswordConfirmModalOpen}
        email={resetEmail}
        onSuccess={handleResetPasswordSuccess}
        onBackToEmail={handleResetPasswordBack}
      />
    </>
  );
}
